import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify/Iconify';
import { woocommerceApi, RecommendedProduct } from 'src/api/woocommerce';
import Image from 'src/components/image/Image';
import { ProductResponseDto } from 'woofwoof-api';

type VisitProductsTabProps = {
  visit?: any;
  onProductsSelected?: (products: RecommendedProduct[]) => void;
};

export default function VisitProductsTab({ visit, onProductsSelected }: VisitProductsTabProps) {
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductResponseDto[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductResponseDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<RecommendedProduct[]>(
    visit?.recommendedProducts || []
  );
  const { enqueueSnackbar } = useSnackbar();

  // Fetch products from WooCommerce
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const response = await woocommerceApi.wooCommerceControllerGetProductsByCategory({
          categoryId: 28,
          page: 1,
          perPage: 100,
        });
        
        if (response.data && response.data.products) {
          setProducts(response.data.products);
          setFilteredProducts(response.data.products);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        enqueueSnackbar(String(t('snackbar.productsLoadError')), {
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [enqueueSnackbar, t]);

  // Initialize selected products from visit data
  useEffect(() => {
    if (visit?.recommendedProducts) {
      setSelectedProducts(visit.recommendedProducts);
    }
  }, [visit]);

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (!query) {
      setFilteredProducts(products);
      return;
    }

    const filtered = products.filter((product) =>
      product.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  // Handle product selection
  const handleProductSelect = (product: ProductResponseDto) => {
    const isSelected = isProductSelected(product.id);
    
    // If product is already selected, remove it
    if (isSelected) {
      const updatedProducts = selectedProducts.filter((p) => p.id !== product.id);
      setSelectedProducts(updatedProducts);
      
      // Update visit object and notify parent
      if (visit) {
        visit.recommendedProducts = updatedProducts;
        if (onProductsSelected) {
          onProductsSelected(updatedProducts);
        }
      }
      return;
    }
    
    // Otherwise, add it to selected products
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.id === product.id)) {
        return prev;
      } else {
        const recommendedProduct: RecommendedProduct = {
          id: product.id,
          name: product.name,
          price: product.price,
          permalink: product.permalink,
          description: product.description,
          images: product.images,
        };
        
        const updatedProducts = [...prev, recommendedProduct];
        
        // Update visit object and notify parent
        if (visit) {
          visit.recommendedProducts = updatedProducts;
          if (onProductsSelected) {
            onProductsSelected(updatedProducts);
          }
        }
        
        return updatedProducts;
      }
    });
  };

  // Check if a product is selected
  const isProductSelected = (productId: number) => {
    return selectedProducts.some((product) => product.id === productId);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search and actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={String(t('visit.searchProductsPlaceholder'))}
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Selected products count */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('visit.productsSelectedCount', {
          count: selectedProducts.length,
        })}
      </Typography>

      {/* Products list */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : filteredProducts.length > 0 ? (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Grid container spacing={2}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Card 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    height: '100%',
                    position: 'relative',
                    border: isProductSelected(product.id) ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                  }}
                >
                  <Checkbox
                    checked={isProductSelected(product.id)}
                    onChange={() => handleProductSelect(product)}
                    sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
                  />
                  <CardMedia
                    component="div"
                    sx={{ height: 140, position: 'relative' }}
                  >
                    {product.images && product.images[0] ? (
                      <Image
                        src={product.images[0].src}
                        alt={product.name}
                        sx={{ height: '100%', width: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          height: '100%', 
                          width: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: 'background.neutral'
                        }}
                      >
                        <Iconify icon="eva:image-fill" sx={{ width: 40, height: 40, opacity: 0.3 }} />
                      </Box>
                    )}
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="subtitle1" component="div" noWrap>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('visit.productPriceLabel', {
                        price: product.price,
                        currency: t('quickBooking.currencySuffix'),
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1 
          }}
        >
          <Image
            disabledEffect
            visibleByDefault
            alt="no products"
            src="/assets/illustrations/laptop-paw-blue.svg"
            sx={{ maxWidth: 180, mb: 3 }}
          />
          <Typography variant="h6" align="center">
            {searchQuery
              ? t('visit.productsNoneFound')
              : t('visit.productsEmpty')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
